/**
 * Усиления «1 из 3» (E4): предложение после пройденной волны, проверка выбора, применение к забежке.
 *
 * Предложение — чистая функция от состояния забежки на старте волны (номер волны, взятые усиления) и сида волны:
 * клиент показывает его сразу после `cleared`, сервер строит то же самое при проверке журнала (contracts/commands,
 * `ChooseBoostCommand`). Отдельный поток ГПСЧ (сид волны с солью) — бой его не трогает, предложение не зависит от
 * того, как шла волна.
 */
import type { BalanceData, BoostDef, BoostRarity, RunBoost, RunState, TimedCommand } from "@newfungame/contracts";
import { createRng } from "../sim/rng";
import { isOfferWave } from "../sim/waves";

/** Сколько вариантов в предложении. */
export const BOOST_OFFER_SIZE = 3;

/** Соль потока ГПСЧ предложения (сид волны XOR соль). Смена соли меняет все предложения — как смена формата журнала. */
export const BOOST_OFFER_SALT = 0x5b0057e4;

/** Вариант предложения. */
export interface BoostOption {
  /** Позиция в предложении — `ChooseBoostCommand.index`. */
  index: number;
  boostId: string;
  rarity: BoostRarity;
  /** Сколько стаков уже взято в этой забежке (до выбора). */
  stacks: number;
  /** Потолок стаков; `null` — бесконечное усиление. */
  maxStacks: number | null;
}

/** Стаки усиления в забежке (0 — не взято). */
export function boostStacks(run: Pick<RunState, "boosts">, boostId: string): number {
  return run.boosts.find((b) => b.boostId === boostId)?.stacks ?? 0;
}

/**
 * Пул предложения после волны `run.wave`: усиления с `minWave ≤ run.wave` и не взятые до `maxStacks`,
 * в порядке данных баланса. Бесконечные сюда не входят — см. {@link endlessBoosts}.
 */
export function eligibleBoosts(balance: BalanceData, run: Pick<RunState, "wave" | "boosts">): BoostDef[] {
  return balance.boosts.filter(
    (b) => b.maxStacks !== null && b.minWave <= run.wave && boostStacks(run, b.id) < b.maxStacks,
  );
}

/** Бесконечные усиления (без потолка стаков) с `minWave ≤ run.wave`: добивают предложение, когда обычный пул мал. */
export function endlessBoosts(balance: BalanceData, run: Pick<RunState, "wave">): BoostDef[] {
  return balance.boosts.filter((b) => b.maxStacks === null && b.minWave <= run.wave);
}

/**
 * Предложение «1 из 3» после пройденной волны `run.wave` (`run` — состояние на старте этой волны, `seed` — её сид).
 * Взвешенный выбор без возвращения по `weight` (веса редкостей — в данных), без повторов. Если в обычном пуле меньше
 * трёх — предлагаются все оставшиеся, а недостающие места добирают бесконечные усиления (тем же потоком ГПСЧ, после
 * обычных: при пуле ≥ 3 предложение то же, что без них). Пусто оба — пустое предложение (выбирать нечего).
 */
export function boostOffer(balance: BalanceData, run: Pick<RunState, "wave" | "boosts">, seed: number): BoostOption[] {
  const out: BoostOption[] = [];
  // бета: «1 из 3» только после последней подволны уровня
  if (!isOfferWave(balance, run.wave)) return out;
  const rng = createRng((seed ^ BOOST_OFFER_SALT) >>> 0);
  const draw = (pool: BoostDef[]): void => {
    while (out.length < BOOST_OFFER_SIZE && pool.length > 0) {
      let total = 0;
      for (const b of pool) total += b.weight;
      let r = rng.int(0, total - 1);
      let k = 0;
      while (k < pool.length - 1 && r >= pool[k]!.weight) r -= pool[k++]!.weight;
      const def = pool.splice(k, 1)[0]!;
      out.push({
        index: out.length,
        boostId: def.id,
        rarity: def.rarity,
        stacks: boostStacks(run, def.id),
        maxStacks: def.maxStacks,
      });
    }
  };
  draw(eligibleBoosts(balance, run));
  draw(endlessBoosts(balance, run));
  return out;
}

/** Почему выбор усиления отклонён. */
export type BoostChoiceRejection =
  /** Волна проиграна — выбирать нельзя. */
  | "wave_lost"
  /** Больше одного `chooseBoost` за волну. */
  | "multiple_choices"
  /** `index` вне предложения или `boostId` не совпал с вариантом на этой позиции. */
  | "not_offered";

export type BoostChoiceCheck = { ok: true; boost: BoostOption | null } | { ok: false; code: BoostChoiceRejection };

/**
 * Проверить выбор усиления в командах волны. Выбора нет — `boost: null` (игрок закрыл окно, можно).
 * `outcome` — итог волны, `run`/`seed` — как в {@link boostOffer}.
 */
export function checkBoostChoice(
  balance: BalanceData,
  run: Pick<RunState, "wave" | "boosts">,
  seed: number,
  outcome: "cleared" | "lost",
  commands: readonly TimedCommand[],
): BoostChoiceCheck {
  const choices = commands.flatMap((c) => (c.command.type === "chooseBoost" ? [c.command] : []));
  if (choices.length === 0) return { ok: true, boost: null };
  if (outcome !== "cleared") return { ok: false, code: "wave_lost" };
  if (choices.length > 1) return { ok: false, code: "multiple_choices" };
  const choice = choices[0]!;
  const option = boostOffer(balance, run, seed)[choice.index];
  if (!option || option.boostId !== choice.boostId) return { ok: false, code: "not_offered" };
  return { ok: true, boost: option };
}

/**
 * Взять усиление: +1 стак (не больше `maxStacks`, если он есть), новое в конец списка. Вход не мутируется.
 * Неизвестное усиление или уже на максимуме — список без изменений.
 */
export function addBoost(balance: BalanceData, boosts: readonly RunBoost[], boostId: string): RunBoost[] {
  const def = balance.boosts.find((b) => b.id === boostId);
  const out = boosts.map((b) => ({ ...b }));
  if (!def) return out;
  const cur = out.find((b) => b.boostId === boostId);
  if (!cur) out.push({ boostId, stacks: 1 });
  else if (def.maxStacks === null || cur.stacks < def.maxStacks) cur.stacks++;
  return out;
}
